import { useState, useCallback } from 'react';

const API_BASE = 'https://commons.wikimedia.org/w/api.php';

const stripHtml = (value = '') => {
  if (!value) return '';
  const text = String(value);
  if (typeof DOMParser === 'undefined') {
    return text.replace(/<[^>]*>/g, '').trim();
  }
  const parsed = new DOMParser().parseFromString(text, 'text/html');
  return parsed.body?.textContent?.trim() || '';
};

const getMetadataValue = (metadata, key) => stripHtml(metadata?.[key]?.value || '');

export const useWikimediaSearch = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const searchImages = useCallback(async (query, limit = 20) => {
    try {
      const params = new URLSearchParams({
        action: 'query',
        list: 'search',
        srsearch: query,
        srnamespace: '6', // File namespace
        srlimit: limit.toString(),
        format: 'json',
        origin: '*'
      });

      const response = await fetch(`${API_BASE}?${params}`);

      if (!response.ok) {
        throw new Error('Search request failed');
      }

      const data = await response.json();

      if (!data.query || !data.query.search) {
        return [];
      }

      return data.query.search.map(result => ({
        id: result.pageid,
        title: result.title,
        snippet: result.snippet
      }));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const getImageDetails = useCallback(async (imageTitle, thumbnailWidth = 400) => {
    try {
      const params = new URLSearchParams({
        action: 'query',
        titles: imageTitle,
        prop: 'imageinfo',
        iiprop: 'url|extmetadata|size|mime',
        iiurlwidth: thumbnailWidth.toString(),
        format: 'json',
        origin: '*'
      });

      const response = await fetch(`${API_BASE}?${params}`);

      if (!response.ok) {
        throw new Error('Failed to fetch image details');
      }

      const data = await response.json();
      const pages = data.query.pages;
      const pageId = Object.keys(pages)[0];
      const page = pages[pageId];

      if (!page.imageinfo || page.imageinfo.length === 0) {
        throw new Error('Image information not available');
      }

      const info = page.imageinfo[0];
      const metadata = info.extmetadata || {};

      const artist = getMetadataValue(metadata, 'Artist');
      const credit = getMetadataValue(metadata, 'Credit');
      const licenseShortName = getMetadataValue(metadata, 'LicenseShortName') || 'Unknown';
      const licenseUrl = getMetadataValue(metadata, 'LicenseUrl');
      const usageTerms = getMetadataValue(metadata, 'UsageTerms');
      const source = getMetadataValue(metadata, 'Source');
      const attribution = getMetadataValue(metadata, 'Attribution') || artist || credit;
      const attributionRequired = metadata.AttributionRequired?.value === 'true';
      const pageUrl = info.descriptionurl || info.descriptionshorturl || '';
      const copyrightStatus = getMetadataValue(metadata, 'CopyrightStatus');
      const permission = getMetadataValue(metadata, 'Permission');
      const restrictions = getMetadataValue(metadata, 'Restrictions');

      const ccData = {
        artist,
        credit,
        attribution,
        attributionRequired,
        licenseShortName,
        licenseUrl,
        usageTerms,
        source,
        pageUrl,
        copyrightStatus,
        permission,
        restrictions
      };

      return {
        title: imageTitle,
        url: info.url,
        thumbUrl: info.thumburl || info.url,
        width: info.width,
        height: info.height,
        size: info.size,
        mime: info.mime,
        filename: imageTitle.replace('File:', ''),
        license: licenseShortName,
        licenseUrl,
        attribution: attribution,
        credit: credit,
        attributionRequired,
        source,
        pageUrl,
        ccData
      };
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const downloadImage = useCallback(async (imageUrl) => {
    try {
      const response = await fetch(imageUrl, {
        mode: 'cors'
      });

      if (!response.ok) {
        throw new Error('Failed to download image');
      }

      return await response.blob();
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  return {
    searchImages,
    getImageDetails,
    downloadImage,
    isLoading,
    error
  };
};
